/* MemberBrief — subscriber-simplified Brief Form.
   3 product image uploads + 1 brand colour (hex picker) + 1 style sentence.
   Used only by Continuity subscribers; one-time Kit buyers use the full
   /intake flow elsewhere. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, X, ArrowRight, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import MemberShell from "@/components/MemberShell";
import { apiMe, authHeaders, formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberBrief() {
    const nav = useNavigate();
    const [email, setEmail] = useState(null);
    const [color, setColor] = useState("#6B4E2E");
    const [styleSentence, setStyleSentence] = useState("");
    const [images, setImages] = useState([]); // string[] of R2 urls
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const me = await apiMe();
                setEmail(me?.member?.email || null);
                // Pre-fill from existing brief if present
                const r = await axios.get(`${API}/member/brief`, { headers: authHeaders() });
                const b = r.data?.brief;
                if (b) {
                    setColor(b.brand_color || "#6B4E2E");
                    setStyleSentence(b.style_sentence || "");
                    setImages(b.product_image_urls || []);
                }
                setLoaded(true);
            } catch (e) {
                if (e?.response?.status === 401) {
                    nav("/member/login?next=/member/brief", { replace: true });
                } else {
                    toast.error("Could not load — please try again");
                }
            }
        })();
    }, [nav]);

    const onPickFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        if (images.length + files.length > 6) {
            toast.error("Max 6 images");
            return;
        }
        try {
            setUploading(true);
            const uploaded = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const r = await axios.post(`${API}/member/brief/upload`, fd, { headers: authHeaders() });
                if (r.data?.url) uploaded.push(r.data.url);
            }
            setImages((prev) => [...prev, ...uploaded].slice(0, 6));
            if (uploaded.length) toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeImage = (idx) => {
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        if (images.length < 1) { toast.error("Please upload at least 1 product image"); return; }
        if (!/^#[0-9A-Fa-f]{6}$/.test(color)) { toast.error("Pick a valid hex colour"); return; }
        if (!styleSentence || styleSentence.trim().length < 4) { toast.error("Tell us your style in one sentence"); return; }
        try {
            setSubmitting(true);
            await axios.post(
                `${API}/member/brief`,
                {
                    brand_color: color,
                    style_sentence: styleSentence.trim(),
                    product_image_urls: images,
                },
                { headers: authHeaders() },
            );
            toast.success("Brief saved — Kaily will use this for your next content.");
            nav("/member", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
        } finally { setSubmitting(false); }
    };

    if (!loaded) {
        return (
            <MemberShell email={email}>
                <div className="text-cafe-mocha italic">Loading your brief…</div>
            </MemberShell>
        );
    }

    return (
        <MemberShell email={email}>
            <div className="max-w-2xl" data-testid="member-brief-page">
                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-2">Brief Form</p>
                <h1 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-3">
                    Tell us about your brand.
                </h1>
                <p className="text-sm text-cafe-mocha mb-10 max-w-xl">
                    Three quick things help Kaily make your monthly content feel like
                    <em> you</em>. Takes about 2 minutes.
                </p>

                {/* 1. Product images */}
                <section className="bg-white border border-border rounded-2xl p-6 mb-6" data-testid="brief-images-section">
                    <p className="text-xs font-bold text-cafe-espresso mb-1.5">
                        1 · Product photos <span className="text-cafe-terracotta">*</span>
                    </p>
                    <p className="text-xs text-cafe-mocha mb-4">Upload 1-6 photos of your actual products (phone shots are fine).</p>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                        {images.map((url, i) => (
                            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-cafe-cream/40 border border-border" data-testid={`brief-image-${i}`}>
                                <img src={url} alt={`product ${i + 1}`} className="w-full h-full object-cover" />
                                <button
                                    onClick={() => removeImage(i)}
                                    className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                                    title="Remove"
                                    data-testid={`brief-image-remove-${i}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                        {images.length < 6 && (
                            <label className="aspect-square rounded-lg border-2 border-dashed border-cafe-olive/40 hover:border-cafe-olive flex flex-col items-center justify-center cursor-pointer bg-cafe-cream/30 transition-colors" data-testid="brief-image-upload">
                                {uploading ? (
                                    <Loader2 className="h-5 w-5 animate-spin text-cafe-olive" />
                                ) : (
                                    <>
                                        <Camera className="h-5 w-5 text-cafe-olive mb-1" />
                                        <span className="text-[10px] uppercase tracking-wider text-cafe-mocha font-bold">Add</span>
                                    </>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={onPickFile}
                                    disabled={uploading}
                                />
                            </label>
                        )}
                    </div>
                    <p className="text-[11px] text-cafe-mocha italic">{images.length}/6 uploaded · max 8MB each</p>
                </section>

                {/* 2. Brand colour */}
                <section className="bg-white border border-border rounded-2xl p-6 mb-6" data-testid="brief-color-section">
                    <p className="text-xs font-bold text-cafe-espresso mb-1.5">
                        2 · Your brand colour <span className="text-cafe-terracotta">*</span>
                    </p>
                    <p className="text-xs text-cafe-mocha mb-4">The single colour that best represents your brand.</p>
                    <div className="flex items-center gap-3">
                        <input
                            type="color"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            className="h-14 w-14 rounded-lg border border-border cursor-pointer"
                            data-testid="brief-color-picker"
                        />
                        <input
                            type="text"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            className="font-mono text-sm border border-border focus:border-cafe-olive rounded-xl px-3 py-2.5 bg-white outline-none uppercase"
                            data-testid="brief-color-text"
                            maxLength={7}
                        />
                        <div className="flex-1 h-14 rounded-lg" style={{ background: color }} />
                    </div>
                </section>

                {/* 3. Style sentence */}
                <section className="bg-white border border-border rounded-2xl p-6 mb-8" data-testid="brief-style-section">
                    <p className="text-xs font-bold text-cafe-espresso mb-1.5">
                        3 · One sentence on your style <span className="text-cafe-terracotta">*</span>
                    </p>
                    <p className="text-xs text-cafe-mocha mb-4">
                        Example: &ldquo;Warm cottagecore café with dried flowers and hand-written labels.&rdquo;
                    </p>
                    <textarea
                        value={styleSentence}
                        onChange={(e) => setStyleSentence(e.target.value)}
                        rows={3}
                        maxLength={400}
                        placeholder="A sentence Kaily can use as the moodboard for your kit."
                        className="w-full border border-border focus:border-cafe-olive rounded-xl px-3.5 py-2.5 text-sm bg-white outline-none"
                        data-testid="brief-style-input"
                    />
                    <p className="text-[11px] text-cafe-mocha text-right mt-1">{styleSentence.length}/400</p>
                </section>

                <button
                    onClick={submit}
                    disabled={submitting}
                    className="w-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="brief-submit-btn"
                >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save my brief <ArrowRight className="h-4 w-4" /></>}
                </button>
            </div>
        </MemberShell>
    );
}
